import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  CircularProgress,
  Divider,
  IconButton
} from "@mui/material";
import {
  Search,
  LocationOn,
  Business,
  AttachMoney,
  CalendarToday,
  Work,
  TrendingUp,
  ArrowForward,
  Visibility
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiUrl, fileUrl } from "../config/api";
import useSEO from "../hooks/useSEO";

const EmployerJobsPage = () => {
  useSEO({ title: 'Мои вакансии | Кабинет работодателя | JobMD' });
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    city: "",
    employment_type: "",
    experience: "",
    remote: false,
    salary_min: "",
    sort: "recent"
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1
  });

  useEffect(() => {
    loadJobs();
  }, [filters, pagination.page]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        action: "public-list",
        page: pagination.page,
        limit: pagination.limit,
        sort: filters.sort
      });

      if (filters.city) params.append("city", filters.city);
      if (filters.employment_type) params.append("employment_type", filters.employment_type);
      if (filters.experience) params.append("experience", filters.experience);
      if (filters.remote) params.append("remote", "true");
      if (filters.salary_min) params.append("salary_min", filters.salary_min);

      const response = await fetch(`${apiUrl("employer_jobs.php")}?${params}`);
      const data = await response.json();

      if (data.success) {
        setJobs(data.jobs);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error("Ошибка загрузки вакансий:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, page: 1 }); // Сбрасываем на первую страницу при изменении фильтров
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadJobs();
  };

  const formatSalary = (job) => {
    if (!job.salary_from && !job.salary_to) return "Зарплата не указана";
    
    const from = job.salary_from ? `${job.salary_from}` : "";
    const to = job.salary_to ? `${job.salary_to}` : "";
    const currency = job.salary_currency || "USD";
    const period = job.salary_period === "month" ? "мес." : 
                   job.salary_period === "year" ? "год" : 
                   job.salary_period;
    
    if (from && to) {
      return `${from} - ${to} ${currency} / ${period}`;
    } else if (from) {
      return `от ${from} ${currency} / ${period}`;
    } else if (to) {
      return `до ${to} ${currency} / ${period}`;
    }
    
    return "Зарплата не указана";
  };

  const getEmploymentTypeText = (type) => {
    switch (type) {
      case 'full-time': return 'Полный день';
      case 'part-time': return 'Частичная занятость';
      case 'contract': return 'Контракт';
      case 'internship': return 'Стажировка';
      case 'remote': return 'Удаленная работа';
      case 'hybrid': return 'Гибридный формат';
      default: return type;
    }
  };

  const getExperienceText = (level) => {
    switch (level) {
      case 'intern': return 'Стажер';
      case 'junior': return 'Junior';
      case 'middle': return 'Middle';
      case 'senior': return 'Senior';
      case 'lead': return 'Lead';
      case 'any': return 'Любой опыт';
      default: return level;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Сегодня";
    if (diffDays === 1) return "Вчера";
    if (diffDays < 7) return `${diffDays} дня назад`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} недели назад`;
    
    return date.toLocaleDateString();
  };

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", p: { xs: 2, md: 3 } }}>
      {/* Заголовок */}
      <Box mb={4}>
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          Вакансии от компаний 🚀
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Прямые предложения от работодателей с детальной информацией и контактами
        </Typography>
      </Box>

      {/* Фильтры */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <form onSubmit={handleSearch}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Город, страна или компания"
                  value={filters.city}
                  onChange={(e) => handleFilterChange("city", e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Тип занятости</InputLabel>
                  <Select
                    value={filters.employment_type}
                    onChange={(e) => handleFilterChange("employment_type", e.target.value)}
                    label="Тип занятости"
                  >
                    <MenuItem value="">Все типы</MenuItem>
                    <MenuItem value="full-time">Полный день</MenuItem>
                    <MenuItem value="part-time">Частичная занятость</MenuItem>
                    <MenuItem value="contract">Контракт</MenuItem>
                    <MenuItem value="internship">Стажировка</MenuItem>
                    <MenuItem value="remote">Удаленная работа</MenuItem>
                    <MenuItem value="hybrid">Гибридный формат</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Опыт</InputLabel>
                  <Select
                    value={filters.experience}
                    onChange={(e) => handleFilterChange("experience", e.target.value)}
                    label="Опыт"
                  >
                    <MenuItem value="">Любой опыт</MenuItem>
                    <MenuItem value="intern">Стажер</MenuItem>
                    <MenuItem value="junior">Junior</MenuItem>
                    <MenuItem value="middle">Middle</MenuItem>
                    <MenuItem value="senior">Senior</MenuItem>
                    <MenuItem value="lead">Lead</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  startIcon={<Search />}
                >
                  Найти
                </Button>
              </Grid>
            </Grid>
            
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Минимальная зарплата"
                  value={filters.salary_min}
                  onChange={(e) => handleFilterChange("salary_min", e.target.value)}
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Сортировка</InputLabel>
                  <Select
                    value={filters.sort}
                    onChange={(e) => handleFilterChange("sort", e.target.value)}
                    label="Сортировка"
                  >
                    <MenuItem value="recent">Сначала новые</MenuItem>
                    <MenuItem value="salary_desc">По убыванию зарплаты</MenuItem>
                    <MenuItem value="salary_asc">По возрастанию зарплаты</MenuItem>
                    <MenuItem value="views">По популярности</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setFilters({
                      city: "",
                      employment_type: "",
                      experience: "",
                      remote: false,
                      salary_min: "",
                      sort: "recent"
                    });
                  }}
                >
                  Сбросить фильтры
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Результаты поиска */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : jobs.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <Work sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Вакансии не найдены
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Попробуйте изменить параметры поиска или зайдите позже
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setFilters({
                  city: "",
                  employment_type: "",
                  experience: "",
                  remote: false,
                  salary_min: "",
                  sort: "recent"
                });
              }}
            >
              Сбросить фильтры
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Typography variant="h6" mb={2}>
            Найдено {pagination.total} вакансий
          </Typography>
          
          <Grid container spacing={3}>
            {jobs.map((job) => (
              <Grid item xs={12} key={job.id}>
                <Card 
                  sx={{ 
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6
                    }
                  }}
                  onClick={() => navigate(`/employer-job/${job.id}`)}
                >
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <Box display="flex" alignItems="center" mb={1}>
                          {job.company_logo && (
                            <Box
                              component="img"
                              src={job.company_logo ? (job.company_logo.startsWith('http') ? job.company_logo : fileUrl(job.company_logo)) : ''}
                              alt={job.company_name}
                              sx={{ width: 50, height: 50, borderRadius: 1, mr: 2 }}
                            />
                          )}
                          <Box>
                            <Typography variant="h6" fontWeight="bold">
                              {job.title}
                            </Typography>
                            <Typography variant="body1" color="primary">
                              {job.company_name}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                          <Chip 
                            icon={<LocationOn />} 
                            label={job.location_city || "Не указано"} 
                            size="small" 
                          />
                          <Chip 
                            label={getEmploymentTypeText(job.employment_type)} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                          <Chip 
                            label={getExperienceText(job.experience_level)} 
                            size="small" 
                          />
                          {job.remote_allowed && (
                            <Chip 
                              label="Удаленно" 
                              size="small" 
                              color="success" 
                            />
                          )}
                        </Stack>
                        
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {job.description.length > 200 
                            ? `${job.description.substring(0, 200)}...` 
                            : job.description}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                          <Typography variant="h6" color="primary" gutterBottom>
                            {formatSalary(job)}
                          </Typography>
                          
                          <Stack spacing={1} mb={2}>
                            <Typography variant="body2" color="text.secondary">
                              <Visibility sx={{ verticalAlign: "middle", mr: 1, fontSize: 16 }} />
                              {job.views_count || 0} просмотров
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <CalendarToday sx={{ verticalAlign: "middle", mr: 1, fontSize: 16 }} />
                              {formatDate(job.published_at)}
                            </Typography>
                          </Stack>
                          
                          <Button
                            variant="contained"
                            endIcon={<ArrowForward />}
                            fullWidth
                          >
                            Подробнее
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Пагинация */}
          {pagination.pages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={(e, page) => setPagination({ ...pagination, page })}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}

      {/* Информационный блок */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom>
                Вы работодатель? 🏢
              </Typography>
              <Typography variant="body1" paragraph>
                Разместите вакансию на нашей платформе и найдите лучших кандидатов.
                Получите доступ к дашборду с аналитикой и управляйте откликами в одном месте.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  const userId = localStorage.getItem("userId");
                  if (userId) {
                    navigate("/employer/dashboard");
                  } else {
                    // Показать модальное окно авторизации
                    alert("Для доступа к кабинету работодателя необходимо авторизоваться");
                  }
                }}
              >
                Разместить вакансию
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center" }}>
                <Business sx={{ fontSize: 100, color: "primary.main", opacity: 0.2 }} />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmployerJobsPage;